import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { IssueModel } from 'src/app/models/api/issue.model';
import { IssuesSupportedActions } from './issuesSupportedActions';
import { IssuesHttpService } from '../../httpServices/issues/issues-http.service';
import { LoggerService } from '../../infra/logger/logger.service';
import { LabelsProviderPipe } from 'src/app/pipes/labels-provider/labels-provider.pipe';
import { EmailPlaceholder } from '../common/common-actions.service';

@Injectable({
  providedIn: 'root',
})
export class IssuesActionsService {
  public readonly haveActions: boolean = true;

  private readonly _issueEmailPlaceholders: EmailPlaceholder[];

  constructor(
    private readonly _issuesHttpService: IssuesHttpService,
    private readonly _logger: LoggerService,
    private readonly _labelProvider: LabelsProviderPipe
  ) {
    this._issueEmailPlaceholders = [
      {
        label: this._labelProvider.transform('emailTemplatePlaceholders.issueName.label'),
        description: this._labelProvider.transform('emailTemplatePlaceholders.issueName.description'),
      },
      {
        label: this._labelProvider.transform('emailTemplatePlaceholders.issueSeverity.label'),
        description: this._labelProvider.transform('emailTemplatePlaceholders.issueSeverity.description'),
      },
      {
        label: this._labelProvider.transform('emailTemplatePlaceholders.issueTaxonomy.label'),
        description: this._labelProvider.transform('emailTemplatePlaceholders.issueTaxonomy.description'),
      },
      {
        label: this._labelProvider.transform('emailTemplatePlaceholders.issueDescription.label'),
        description: this._labelProvider.transform('emailTemplatePlaceholders.issueDescription.description'),
      },
      {
        label: this._labelProvider.transform('emailTemplatePlaceholders.issueActorName.label'),
        description: this._labelProvider.transform('emailTemplatePlaceholders.issueActorName.description'),
      },
      {
        label: this._labelProvider.transform('emailTemplatePlaceholders.issueActorEmail.label'),
        description: this._labelProvider.transform('emailTemplatePlaceholders.issueActorEmail.description'),
      },
      {
        label: this._labelProvider.transform('emailTemplatePlaceholders.issueRuleId.label'),
        description: this._labelProvider.transform('emailTemplatePlaceholders.issueRuleId.description'),
      },
      {
        label: this._labelProvider.transform('emailTemplatePlaceholders.issueResourceName.label'),
        description: this._labelProvider.transform('emailTemplatePlaceholders.issueResourceName.description'),
      },
    ];
  }

  public getIssueEmailPlaceholders(): EmailPlaceholder[] {
    return this._issueEmailPlaceholders;
  }

  /**
   * Handle action clicked for an issue
   * @param action The action to perform
   * @param issue The issue to perform the action on
   * @returns Observable of the updated issue or undefined
   */
  public handleActionClicked(action: IssuesSupportedActions, issue: IssueModel): Observable<IssueModel | undefined> {
    const integrationId = issue.integration?.id || issue.integrationId;

    if (!integrationId || !issue.id) {
      this._logger.error('IssuesActionsService handleActionClicked: Missing integration or issue ID');
      return of(undefined);
    }

    if (action === IssuesSupportedActions.Resolve) {
      return this._handleResolveActionClicked(issue);
    }

    if (action === IssuesSupportedActions.Reopen) {
      return this._handleReopenActionClicked(issue);
    }

    this._logger.error(`IssuesActionsService handleActionClicked: ${action} support not implemented.`);
    return of(undefined);
  }

  /**
   * Resolve an issue
   * @param integrationId Integration ID
   * @param issueId Issue ID
   * @returns Observable of the updated issue
   */
  public resolveIssue(integrationId: string, issueId: string): Observable<IssueModel> {
    return this._issuesHttpService.resolveIssue(integrationId, issueId);
  }

  /**
   * Reopen an issue
   * @param integrationId Integration ID
   * @param issueId Issue ID
   * @returns Observable of the updated issue
   */
  public reopenIssue(integrationId: string, issueId: string): Observable<IssueModel> {
    return this._issuesHttpService.reopenIssue(integrationId, issueId);
  }

  private _handleResolveActionClicked(issue: IssueModel): Observable<IssueModel | undefined> {
    const integrationId = issue.integration?.id || issue.integrationId;

    if (!integrationId || !issue.id) {
      this._logger.error('IssuesActionsService: Missing integration or issue ID for resolve action');
      return of(undefined);
    }

    return this.resolveIssue(integrationId, issue.id);
  }

  private _handleReopenActionClicked(issue: IssueModel): Observable<IssueModel | undefined> {
    const integrationId = issue.integration?.id || issue.integrationId;

    if (!integrationId || !issue.id) {
      this._logger.error('IssuesActionsService: Missing integration or issue ID for reopen action');
      return of(undefined);
    }

    return this.reopenIssue(integrationId, issue.id);
  }
}
